import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
/* Components */
import { Modal, Form, InputGroup } from "react-bootstrap";
import WrongNetworkAlert from "./WrongNetworkAlert";
import TxButton from "../CommonForBoth/TxButton";
import CustomTooltip from "@/components/CommonForBoth/CustomTooltip"; // Importamos tu componente
/* Helpers */
import { strToDec, strToDecZ, strToBN } from "@/helpers/number_format_helper";
import { isTransactionValid } from "@/helpers/store_helper";
import { hasCooler, ZERO_ADDRESS } from "@/helpers/cooler_helper";
import { formatSecondsToDaysValue } from "@/helpers/date_helper";
import { useModalTxError } from "@/hooks/useModalTxError";
/* Store */
import { selectEthCallMultiple, selectLastTransact, addRemoveEthSub } from "@ensuro/ethereum-store";
import { selectCurrent } from "@store/eTokens/selectors";
import { selectChainId } from "@/store/user/selectors";
/* i18n */
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { chain, currency } from "@/config";
import "./style.scss";

import { ethers } from "ethers";

const makeEthCalls = function (etkAddress) {
  return [
    { address: etkAddress, abi: "EToken", method: "totalWithdrawable", args: [] },
    { address: etkAddress, abi: "EToken", method: "cooler", args: [] },
  ];
};

const makeCoolerCall = (coolerAddress, etkAddress, userAddress) => {
  return hasCooler(coolerAddress)
    ? [
        {
          address: coolerAddress,
          abi: "Cooler",
          method: "cooldownPeriod",
          args: [etkAddress, userAddress || ZERO_ADDRESS, 0],
        },
      ]
    : [];
};

const WithdrawModal = ({
  t,
  user,
  userAddress,
  lastTransaction,
  show,
  onHide,
  balance,
  poolAddress,
  etkAddress,
  chainId,
  totalWithrawable,
  coolerAddress,
  cooldownPeriod,
  hasValidCooler,
}) => {
  const dispatch = useDispatch();
  const { errorToShow, clearError, wrapDispatch } = useModalTxError(lastTransaction);
  const [userAmount, setUserAmount] = useState("");
  const ready = balance.state === "LOADED" && chainId === chain.id;

  const handleHide = () => {
    clearError();
    onHide();
  };

  const invalidBalance = () => {
    return (
      !balance.value ||
      (userAmount !== ethers.MaxUint256 && strToDec(userAmount) && strToDec(userAmount).gt(balance.value))
    );
  };

  const exceedsTotalWithdrawable = () => {
    return (
      userAmount !== ethers.MaxUint256 &&
      totalWithrawable &&
      totalWithrawable.value &&
      strToDec(userAmount) &&
      strToDec(userAmount).gt(totalWithrawable.value) // The user is trying to withdraw more than available
    );
  };

  const dispatchWithdraw = () => {
    if (!isTransactionValid(lastTransaction) || !userAddress) return;
    const amountBN = userAmount === ethers.MaxUint256 ? ethers.MaxUint256 : strToBN(userAmount);
    if (amountBN == null) return;
    dispatch({
      type: "ETH_TRANSACT",
      address: poolAddress,
      abi: "PolicyPool",
      method: "withdraw",
      args: [etkAddress, amountBN, userAddress, userAddress],
    });
  };

  useEffect(() => {
    return addRemoveEthSub(dispatch, "withdrawModal", makeEthCalls(etkAddress));
  }, [dispatch, etkAddress]);

  useEffect(() => {
    if (!hasValidCooler) return;
    const coolerCalls = makeCoolerCall(coolerAddress, etkAddress, userAddress);
    return addRemoveEthSub(dispatch, "withdrawModalCooler", coolerCalls);
  }, [dispatch, etkAddress, coolerAddress, userAddress, hasValidCooler]);

  const getMin = () => {
    if (balance && balance.value && totalWithrawable && totalWithrawable.value) {
      return Math.min(balance.value.toString(), totalWithrawable.value.toString());
    }
    return 0;
  };

  return (
    <Modal show={show} centered onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>{t("Withdraw ", { currency })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {chainId !== chain.id && <WrongNetworkAlert />}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("Amount")}</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="0.00"
                id="withdraw-amount"
                isInvalid={
                  userAmount !== "" && (!strToDecZ(userAmount) || invalidBalance() || exceedsTotalWithdrawable())
                }
                value={userAmount === ethers.MaxUint256 ? getMin() : userAmount}
                onChange={(e) => setUserAmount(e.target.value || "")}
              />
              <InputGroup.Text>{currency.name}</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {!strToDecZ(userAmount) && t("Please provide a valid amount.")}
                {invalidBalance() ? (
                  <span>{t("Your balance is not enough.")}</span>
                ) : (
                  exceedsTotalWithdrawable() && (
                    <span>{t("The maximum you can withdraw is ", { min: getMin(), currency })}</span>
                  )
                )}
              </Form.Control.Feedback>
            </InputGroup>
            <p className="align-end">
              {t("Your balance")}: {balance && balance.value ? balance.value.toString() : ""}
              <strong onClick={() => setUserAmount(ethers.MaxUint256)} className="btn-text">
                {t("MAX")}
              </strong>
            </p>
          </Form.Group>

          <div className="mt-4 pt-2 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <span className="text-muted me-1">{t("Cooldown period")}</span>
                <CustomTooltip
                  text={t(
                    "You can only withdraw your assets after the cooldown period ends and the unstake window is active."
                  )}
                  placement="top"
                >
                  <span className="fa fa-info-circle" />
                </CustomTooltip>
              </div>

              <span className="fw-bold">
                {!hasValidCooler || formatSecondsToDaysValue(cooldownPeriod?.value) === 0
                  ? t("Immediate")
                  : `${formatSecondsToDaysValue(cooldownPeriod.value).toFixed(2)} ${t("days")}`}
              </span>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <em style={{ color: "red" }}>{errorToShow ?? ""}</em>
        <TxButton
          chainId={chainId}
          onClick={wrapDispatch(dispatchWithdraw)}
          disabled={
            !ready ||
            !strToDecZ(userAmount) ||
            invalidBalance() ||
            exceedsTotalWithdrawable() ||
            (user && !user.canTransact)
          }
          text={
            !hasValidCooler || formatSecondsToDaysValue(cooldownPeriod?.value) === 0
              ? t("Withdraw")
              : t("Schedule Withdrawal")
          }
        />
      </Modal.Footer>
      {user && !user.canTransact && (
        <p className="read-only">{t("Read-only mode. Connect to a wallet to perform transactions.")}</p>
      )}
    </Modal>
  );
};

WithdrawModal.propTypes = {
  t: PropTypes.any,
};

const mapStateToProps = (state) => {
  const user = state.UserReducer;
  const etk = selectCurrent(state.ETokensReducer);
  const etkAddress = etk.address;
  const url = new URL(etk.pool);
  const poolAddress = url.pathname.split("/")[3];
  const lastTransaction = selectLastTransact(state.EthereumReducer);
  const chainId = selectChainId(state.UserReducer);
  const userAddress = user?.address;
  const ethCalls = makeEthCalls(etkAddress);
  const [totalWithrawable, cooler] = selectEthCallMultiple(state.EthereumReducer, ethCalls);
  const coolerAddress = cooler?.value;
  const hasValidCooler = hasCooler(coolerAddress);
  const coolerCall = makeCoolerCall(coolerAddress, etkAddress, userAddress);
  const [cooldownPeriod] = selectEthCallMultiple(state.EthereumReducer, coolerCall);

  return {
    user,
    userAddress,
    lastTransaction,
    etkAddress,
    poolAddress,
    chainId,
    totalWithrawable,
    coolerAddress,
    cooldownPeriod,
    hasValidCooler,
  };
};

export default connect(mapStateToProps)(withTranslation()(WithdrawModal));
